// Invitation Link Section Component

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, CopyButton } from '../../../components/shared';
import { telegramApi } from '../api';
import { InvitationTokensResponse } from '../api/types';

export const InvitationLinkSection: React.FC = () => {
  const { t } = useTranslation();
  const [invitationData, setInvitationData] =
    useState<InvitationTokensResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchInvitationTokens = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await telegramApi.getInvitationTokens();
        setInvitationData(data);
      } catch (err) {
        console.error('Failed to fetch invitation tokens:', err);
        setError('Failed to load invitation link');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvitationTokens();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">{t('common.loading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-500 text-4xl mb-4">⚠️</div>
        <p className="text-gray-600 mb-4">{error}</p>
        <Button
          onClick={() => window.location.reload()}
          variant="outline"
          size="sm"
        >
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  if (!invitationData || !invitationData.tokens.length) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-400 text-4xl mb-4">🔗</div>
        <p className="text-gray-600">
          {t('settings.telegram.invitation.noTokens')}
        </p>
      </div>
    );
  }

  const invitationLink = invitationData.linkTemplate.replace(
    '{token}',
    invitationData.tokens[0].token
  );

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
          <span className="text-lg mr-2">🔗</span>
          {t('settings.telegram.invitation.linkTitle')}
        </h4>
        <p className="text-xs text-gray-500 mb-3">
          {t('settings.telegram.invitation.linkDescription')}
        </p>

        <div className="space-y-3">
          {/* Invitation Link Display */}
          <div className="bg-gray-50 rounded-lg p-3 border">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-600 mb-1">
                  {t('settings.telegram.invitation.linkLabel')}
                </p>
                <p className="text-xs text-gray-800 font-mono break-all">
                  {invitationLink}
                </p>
              </div>
              <CopyButton
                text={invitationLink}
                variant="outline"
                size="sm"
                className="ml-3 flex-shrink-0"
                copyLabel={t('common.copy')}
                copiedLabel={t('common.copied')}
              />
            </div>
          </div>

          {/* Token Info */}
          <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
            <div className="flex items-center justify-between text-sm">
              <span className="text-blue-700">
                {t('settings.telegram.invitation.usageCount')}:{' '}
                {invitationData.tokens[0].usageCount}
              </span>
              {invitationData.tokens[0].usageLimit && (
                <span className="text-blue-600">
                  / {invitationData.tokens[0].usageLimit}
                </span>
              )}
            </div>
            <div className="text-xs text-blue-600 mt-1">
              {t('settings.telegram.invitation.expiresAt')}:{' '}
              {new Date(
                invitationData.tokens[0].expiresAt
              ).toLocaleDateString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
